import { AnimatePresence, motion } from 'framer-motion';

import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import CircularProgress from '@mui/joy/CircularProgress';

import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';

import { MOTION, ellipsis } from './chatTheme';

// Больше столбиков в полоске не рисуем: они становятся тоньше волоса и
// перестают что-либо показывать. Точное число всё равно есть справа.
const MAX_SEGMENTS = 8;

/**
 * Полоска закреплённого сообщения над лентой.
 *
 * Устроена как в телеграме, и по той же причине: закреплённых обычно
 * несколько, но место есть ровно под одно. Слева — столбики по числу
 * закреплённых, подсвечен текущий; нажатие на саму полоску переводит к
 * сообщению, а следующее нажатие показывает следующий закреплённый. Так
 * до нужного добираешься, ни разу не открывая список.
 *
 * Кнопка справа открывает полный список — он нужен, когда закреплённых
 * много и листать их по одному долго.
 */
export default function PinnedBar({
  pinned = [],
  index = 0,
  onCycle,
  onOpenList,
  onJump,
  busy = false,
}) {
  const total = pinned.length;
  const current = total > 0 ? pinned[Math.min(index, total - 1)] : null;

  return (
    <AnimatePresence initial={false}>
      {current && (
        <Box
          component={motion.div}
          key="pinned-bar"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: MOTION.base, ease: MOTION.ease }}
          sx={(theme) => ({
            flexShrink: 0,
            overflow: 'hidden',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(14px)',
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(20,24,35,0.72)'
              : 'rgba(255,255,255,0.78)',
            position: 'relative',
            zIndex: 3,
          })}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              px: { xs: 1, md: 1.75 },
              py: 0.75,
              minWidth: 0,
            }}
          >
            {/* Столбики: сколько закреплено и где мы сейчас */}
            <Stack
              direction="column"
              spacing="2px"
              sx={{
                width: 3, alignSelf: 'stretch', flexShrink: 0,
                minHeight: 30, justifyContent: 'stretch',
              }}
            >
              {Array.from({ length: Math.min(total, MAX_SEGMENTS) }).map((_, i) => {
                // Когда закреплённых больше, чем столбиков, окно столбиков
                // едет за текущим — иначе на девятом подсвечивать было бы
                // нечего.
                const offset = total > MAX_SEGMENTS
                  ? Math.min(Math.max(index - (MAX_SEGMENTS - 1), 0), total - MAX_SEGMENTS)
                  : 0;
                const active = i + offset === Math.min(index, total - 1);
                return (
                  <Box
                    key={i}
                    component={motion.span}
                    animate={{ opacity: active ? 1 : 0.28 }}
                    transition={{ duration: MOTION.fast }}
                    sx={{
                      flex: 1, minHeight: 4, borderRadius: '2px',
                      backgroundColor: 'primary.500',
                    }}
                  />
                );
              })}
            </Stack>

            <Box
              component={motion.div}
              whileTap={{ scale: 0.99 }}
              onClick={() => { onJump?.(current.id); onCycle?.(); }}
              sx={{
                flex: 1, minWidth: 0,
                cursor: 'pointer',
                borderRadius: '10px',
                px: 0.75, py: 0.25,
                transition: 'background-color .16s',
                '&:hover': { backgroundColor: 'background.level1' },
              }}
            >
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', minWidth: 0 }}>
                <PushPinRoundedIcon
                  sx={{ fontSize: 12, color: 'primary.500', flexShrink: 0, transform: 'rotate(35deg)' }}
                />
                <Typography
                  level="body-xs"
                  sx={{ fontWeight: 750, fontSize: 11, color: 'primary.500', flexShrink: 0 }}
                >
                  {total > 1
                    ? `Закреплённое ${Math.min(index, total - 1) + 1} из ${total}`
                    : 'Закреплённое сообщение'}
                </Typography>
                {busy && <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} />}
              </Stack>

              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'baseline', minWidth: 0 }}>
                {current.author && (
                  <Typography
                    level="body-xs"
                    sx={{ fontSize: 11.5, fontWeight: 650, color: 'text.secondary', flexShrink: 0 }}
                  >
                    {current.author.split(' ')[1] || current.author}:
                  </Typography>
                )}
                <Typography
                  level="body-xs"
                  sx={{ ...ellipsis, fontSize: 12, color: 'text.secondary', flex: 1, minWidth: 0 }}
                >
                  {current.content || 'Сообщение'}
                </Typography>
              </Stack>
            </Box>

            {total > 1 && (
              <Tooltip title="Все закреплённые" size="sm">
                <IconButton
                  size="sm" variant="plain" color="neutral"
                  onClick={(e) => { e.stopPropagation(); onOpenList?.(); }}
                  sx={{
                    flexShrink: 0, borderRadius: '50%',
                    '&:hover': { backgroundColor: 'background.level1' },
                  }}
                >
                  <FormatListBulletedRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      )}
    </AnimatePresence>
  );
}
